import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../http';

/**
 * Per-event document assignment tab inside EditEvent.
 * Documents are managed in the global library (/admin/documents) and assigned here.
 */
export default function EventDocuments({ eventId }) {
  const [assignments, setAssignments] = useState([]);
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState('');
  const [isRequired, setIsRequired] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [assignRes, libRes] = await Promise.all([
        api.get(`/api/registration/events/${eventId}/documents`),
        api.get('/api/documents')
      ]);
      setAssignments(assignRes.data);
      setLibrary(libRes.data);
    } catch {
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  // Documents not yet assigned to this event
  const assignedIds = new Set(assignments.map((a) => a.id));
  const available = library.filter((d) => !assignedIds.has(d.id));

  const handleAssign = async () => {
    if (!selectedDocId) { setError('Please select a document'); return; }
    setSaving(true);
    setError('');
    try {
      await api.post(`/api/registration/events/${eventId}/documents`, {
        document_id: parseInt(selectedDocId),
        is_required: isRequired,
        display_order: assignments.length
      });
      setSelectedDocId('');
      setIsRequired(true);
      setShowAssignForm(false);
      await loadData();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to assign document');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (assignment) => {
    if (!window.confirm(`Remove "${assignment.title}" from this event? The document will remain in the library.`)) return;
    try {
      await api.delete(`/api/registration/events/${eventId}/documents/${assignment.assignment_id}`);
      setAssignments((prev) => prev.filter((a) => a.assignment_id !== assignment.assignment_id));
    } catch {
      setError('Failed to remove document');
    }
  };

  if (loading) return <div className="event-docs-loading">Loading…</div>;

  return (
    <div className="event-documents">
      <div className="event-docs-header">
        <div>
          <h3 className="event-docs-title">Signing Documents</h3>
          <p className="event-docs-subtitle">
            Documents candidates must sign during check-in via the QR code link.{' '}
            <Link to="/admin/documents" style={{ color: '#f97316' }}>
              Manage document library →
            </Link>
          </p>
        </div>
        {!showAssignForm && available.length > 0 && (
          <button className="btn btn-primary btn-sm" onClick={() => setShowAssignForm(true)}>
            + Assign Document
          </button>
        )}
      </div>

      {error && <p className="event-docs-error">{error}</p>}

      {assignments.length === 0 && !showAssignForm ? (
        <div className="event-docs-empty">
          <p>No documents assigned. Candidates will be registered without a signing step.</p>
          {library.length === 0 ? (
            <p>
              <Link to="/admin/documents">Create documents in the library</Link> first, then assign them here.
            </p>
          ) : (
            <button className="btn btn-outline btn-sm" onClick={() => setShowAssignForm(true)}>
              Assign a Document
            </button>
          )}
        </div>
      ) : (
        <div className="event-docs-list">
          {assignments.map((doc) => (
            <div key={doc.assignment_id} className="event-doc-card">
              <div className="event-doc-info">
                <span className="event-doc-name">{doc.title}</span>
                <div className="event-doc-meta">
                  <span className={`doc-type-badge doc-type-${doc.content_type}`}>
                    {doc.content_type.toUpperCase()}
                  </span>
                  {doc.is_required && <span className="doc-required-badge">Required</span>}
                </div>
              </div>
              <button className="btn btn-danger btn-sm" onClick={() => handleRemove(doc)}>
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      {showAssignForm && (
        <div className="event-doc-form">
          <h4>Assign Document</h4>

          {available.length === 0 ? (
            <p style={{ color: '#6b7280', fontSize: 14 }}>
              All library documents are already assigned to this event.{' '}
              <Link to="/admin/documents">Add more to the library →</Link>
            </p>
          ) : (
            <>
              <div className="form-group">
                <label className="form-label">Document *</label>
                <select
                  className="form-input"
                  value={selectedDocId}
                  onChange={(e) => setSelectedDocId(e.target.value)}
                >
                  <option value="">— Select a document —</option>
                  {available.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.title} ({d.content_type.toUpperCase()})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group form-check">
                <label className="form-check-label">
                  <input
                    type="checkbox"
                    checked={isRequired}
                    onChange={(e) => setIsRequired(e.target.checked)}
                  />
                  Required — candidate must sign before registration is complete
                </label>
              </div>

              <div className="form-actions">
                <button className="btn btn-primary" onClick={handleAssign} disabled={saving}>
                  {saving ? 'Saving…' : 'Assign'}
                </button>
                <button className="btn btn-secondary" onClick={() => { setShowAssignForm(false); setError(''); }}>
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
